import { kebabCase } from "@nwt/norce-pwa/stringManipulation";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { BreadcrumbsCategory } from "~/types/composables/useCategoryTypes";
import {
	buildParentChain,
	findCategoryNode,
	resolveCategoryPath
} from "~/utils/categories/categoryTreeIndex";
import { toBreadcrumbChain } from "~/utils/categories/toBreadcrumbChain";

/*
	`kebabCase` is imported rather than taken from `useNorcePwa()` on purpose.

	It is a pure string function, but reaching it through the composable made it
	require a Nuxt instance — and callers do not always have one. The category
	page builds its canonical path inside a `useAsyncData` handler, where the
	server has already dropped the context past the first await: the call threw,
	`useAsyncData` stored the error, and because Nuxt does not re-run a handler
	that failed on the server, the page hydrated with no products. Client-side
	navigation kept working, so it looked fine until someone refreshed.

	Imported here, nothing below depends on where it is called from.
*/

export const useCategory = () => {
	/**
	 * The category with its `Children` subtree and heavy per-node fields
	 * blanked, plus a fresh `{ Name, Code, Parent }` chain up to the root.
	 * A new object on every call, never the tree node itself: callers put it in
	 * Pinia, which would otherwise wrap and expose the shared tree. The copy is
	 * one level deep: `Children` and `Parametrics`, the only nested values the
	 * `Category` type declares, are replaced rather than shared.
	 */
	const getCategoryDetailsById = (
		categoryTree: Category[],
		id: number,
		keyToSearchFor: "CategoryId" | "Id"
	): BreadcrumbsCategory => {
		const category = findCategoryNode(categoryTree, id, keyToSearchFor);
		// null at runtime when the tree cannot place the id, as it always was.
		// The declared type is kept so the callers' types (and the forked
		// pageStore setters they feed) do not move.
		if (!category) return null as unknown as BreadcrumbsCategory;
		return toBreadcrumbChain({
			...category,
			Parent: buildParentChain(categoryTree, category)
		});
	};

	const iterateOverCategoryDetails = (
		categoryDetails: BreadcrumbsCategory | Partial<BreadcrumbsCategory>,
		slugs: string[] = []
	) => {
		if (categoryDetails) {
			slugs.unshift(kebabCase(categoryDetails.Name!));
			if (categoryDetails.Parent) {
				iterateOverCategoryDetails(categoryDetails.Parent, slugs);
			}
		}
		return slugs.join("/");
	};

	const createCategoryLink = ({
		category
	}: {
		category: Pick<Category, "CategoryId">;
	}) => {
		const { locale } = useI18n();

		// Read through the store so the link re-renders when the tree arrives;
		// the index itself walks the raw tree, once per tree (NF-390).
		return resolveCategoryPath(
			useCategoryStore().getCategoryTree[locale.value],
			category.CategoryId
		);
	};
	return {
		getCategoryDetailsById,
		createCategoryLink,
		iterateOverCategoryDetails
	};
};
